import React, { useState, useEffect } from 'react'
import { api, formatCurrency } from '../../utils/api'
import ConditionPicker, { TESTING_CONDITION_OPTIONS } from './ConditionPicker'
import { isElectricalChecklist, testingLevelsFor } from './testingHelpers'
import TradeLinkPicker, { NoTradeSubmitGuard, MAX_MANUAL_LISTING_QUANTITY, linkRequestFields } from './ManualListingTrade'
import ProductTypeSelector from './ProductTypeSelector'
import IncludedItemsPicker from './IncludedItemsPicker.jsx'
import { toggleIncludedItem as toggleIncludedSelection, includedItemsAnswered, includedItemsForSubmit } from './includedItems.js'

// Design System (matching Testing.jsx)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Constants
const PHYSICAL_SCORE_OPTIONS = ['Fair', 'Good', 'Very Good', 'Excellent', 'Mint']
const CHECKLIST_RESULTS = ['OK', 'FAIL', 'N/A']

const PHYSICAL_FIELDS = [
  { key: 'scratching', label: 'Scratching & Scraping' },
  { key: 'dings', label: 'Dings and Dents' },
  { key: 'colouration', label: 'Colouration' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'integrity', label: 'Physical Integrity' }
]

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick, style = {} }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    warning: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s, ...style,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20, light = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${light ? 'rgba(255,255,255,0.3)' : theme.colors.grey200}`,
    borderTopColor: light ? 'white' : theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const Alert = ({ type = 'info', children }) => {
  const types = {
    info: { bg: theme.colors.blueLight, border: theme.colors.imBlue, icon: 'i' },
    warning: { bg: theme.colors.amberLight, border: theme.colors.sunburst, icon: '!' },
    error: { bg: theme.colors.redLight, border: theme.colors.crimson, icon: 'x' },
    success: { bg: theme.colors.greenLight, border: theme.colors.greenOnions, icon: '✓' }
  }
  const t = types[type]
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: theme.radius.md,
      padding: '12px 16px',
      fontSize: '14px',
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    }}>
      <span>{t.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

// Option Button Group
const OptionGroup = ({ options, value, onChange, columns = 5 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '8px' }}>
    {options.map(opt => (
      <button
        key={opt}
        onClick={() => onChange(opt)}
        style={{
          padding: '10px 8px',
          border: `2px solid ${value === opt ? theme.colors.greenOnions : theme.colors.grey200}`,
          borderRadius: theme.radius.md,
          background: value === opt ? theme.colors.greenLight : theme.colors.white,
          fontWeight: value === opt ? 600 : 400,
          fontSize: '13px',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        {opt}
      </button>
    ))}
  </div>
)

function ManualListing() {
  const [step, setStep] = useState('search') // search, entry, testing, success
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)

  // Item details
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [productType, setProductType] = useState('')
  const [testingBucket, setTestingBucket] = useState('')
  const [buyPrice, setBuyPrice] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  // Optional trade line, quantity and reason (unit economics D36)
  const [tradeLink, setTradeLink] = useState(null)
  const [quantity, setQuantity] = useState('1')
  const [reason, setReason] = useState('')

  // Product type selector
  const [productTypes, setProductTypes] = useState(null)
  const [productTypesLoading, setProductTypesLoading] = useState(false)
  const [showProductTypeSelector, setShowProductTypeSelector] = useState(false)

  // Testing state
  const [checklist, setChecklist] = useState(null)
  const [includedItemsOptions, setIncludedItemsOptions] = useState([])
  const [testResult, setTestResult] = useState({
    overallCondition: null,
    physicalCondition: {
      scratching: null,
      dings: null,
      colouration: null,
      cleanliness: null,
      integrity: null
    },
    testingLevel: null,
    checklistResults: {},
    includedItems: [],
    otherAccessories: '',
    notes: '',
    needsRepair: false,
    repairIssues: ''
  })

  // Submission state
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  const isElectrical = isElectricalChecklist(checklist)
  const testingLevelOptions = testingLevelsFor(checklist)
  const productName = `${brand} ${model}`.trim()

  // Search for existing products
  const searchProducts = async () => {
    if (searchQuery.length < 2) return
    setSearchLoading(true)
    setHasSearched(true)
    try {
      const response = await api.get(`/api/warehouse/products/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchResults(response.products || [])
    } catch (err) {
      console.error('Search error:', err)
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  // Select an existing product
  const selectExistingProduct = (product) => {
    setSelectedProduct(product)
    // Parse brand/model from product_name (assuming "Brand Model" format)
    const parts = product.product_name.split(' ')
    if (parts.length >= 2) {
      setBrand(parts[0])
      setModel(parts.slice(1).join(' '))
    } else {
      setBrand(product.product_name)
      setModel('')
    }
    setProductType(product.product_type)
    setStep('entry')
  }

  // Create new product (go to entry form)
  const createNewProduct = () => {
    // Pre-fill brand/model from search query if it looks like "Brand Model"
    const parts = searchQuery.trim().split(' ')
    if (parts.length >= 2) {
      setBrand(parts[0])
      setModel(parts.slice(1).join(' '))
    } else if (parts.length === 1) {
      setBrand(parts[0])
    }
    setSelectedProduct(null)
    setStep('entry')
  }

  const loadProductTypes = async () => {
    if (productTypes) return
    setProductTypesLoading(true)
    try {
      const response = await api.get('/api/warehouse/testing/product-types')
      setProductTypes(response)
    } catch (err) {
      console.error('Failed to load product types:', err)
    } finally {
      setProductTypesLoading(false)
    }
  }

  const openProductTypeSelector = async () => {
    await loadProductTypes()
    setShowProductTypeSelector(true)
  }

  const handleProductTypeSelect = async (type, bucket) => {
    setProductType(type)
    setTestingBucket(bucket)
    setShowProductTypeSelector(false)
    await loadChecklist(type)
  }

  const loadChecklist = async (type) => {
    if (!type) {
      setChecklist(null)
      setIncludedItemsOptions([])
      return
    }
    try {
      const response = await api.get(`/api/warehouse/testing/checklist?productType=${encodeURIComponent(type)}`)
      setChecklist(response)
      setIncludedItemsOptions(response.includedItems || [])
    } catch (err) {
      setChecklist(null)
      setIncludedItemsOptions([])
    }
  }

  const updatePhysicalCondition = (field, value) => {
    setTestResult(prev => ({
      ...prev,
      physicalCondition: { ...prev.physicalCondition, [field]: value }
    }))
  }

  const updateChecklistResult = (key, value) => {
    setTestResult(prev => ({
      ...prev,
      checklistResults: { ...prev.checklistResults, [key]: value }
    }))
  }

  const toggleIncludedItem = (item) => {
    setTestResult(prev => ({
      ...prev,
      includedItems: toggleIncludedSelection(prev.includedItems, item)
    }))
  }

  const canProceedToTesting = () => {
    const units = Number(quantity)
    return brand && model && productType && buyPrice && Number.isInteger(units) && units >= 1 && units <= maxQuantity
  }

  // An extra on a trade (spec 20261021): not on the quote, never paid for.
  const isExtra = tradeLink?.kind === 'extra'
  // A return (Trac #1333): the server takes the trade link and cost from the
  // old product, so a known cost is shown and locked here.
  const isReturn = tradeLink?.kind === 'return'
  const returnCostKnown = isReturn && tradeLink.item.cost_cents != null

  // A linked line bounds the quantity; the server refuses more either way.
  // A return is the one unit that came back.
  const maxQuantity = tradeLink?.kind === 'line' ? tradeLink.line.quantity
    : tradeLink?.kind === 'return' ? 1
    : MAX_MANUAL_LISTING_QUANTITY

  // A picked line fills what staff would otherwise retype. Condition is left
  // alone: a different condition is often why the item is listed by hand.
  // The reason follows the last pick, so the reason and the link always agree.
  const pickTrade = (link) => {
    const wasLocked = isExtra || isReturn
    setTradeLink(link)
    if (link?.kind === 'extra') {
      setBuyPrice('0')
      setReason('trade_extra')
      setTestResult(prev => ({ ...prev, needsRepair: false }))
      return
    }
    if (link?.kind === 'return') {
      setBuyPrice(link.item.cost_cents == null ? '' : String(link.item.cost_cents / 100))
      setReason('return_condition_change')
      setQuantity('1')
      setTestResult(prev => ({ ...prev, needsRepair: false }))
      return
    }
    if (wasLocked) {
      setBuyPrice('')
      setReason('')
    }
    if (!link) return
    if (link.line.brand) setBrand(link.line.brand)
    if (link.line.model) setModel(link.line.model)
    if (link.line.buyPriceCents != null) setBuyPrice(String(link.line.buyPriceCents / 100))
  }

  // Leaving the extra or return reason drops that link.
  const pickReason = (next) => {
    if (isExtra && next !== 'trade_extra') pickTrade(null)
    if (isReturn && next !== 'return_condition_change') pickTrade(null)
    setReason(next)
  }

  const canSubmit = () => {
    if (!testResult.overallCondition) return false
    // W7: the included-items step is required whenever it shows, broken items included.
    if (includedItemsOptions.length > 0 && !includedItemsAnswered(testResult.includedItems, testResult.otherAccessories)) return false
    if (testResult.overallCondition === 'Broken') return true // Can submit broken items

    const physicalComplete = Object.values(testResult.physicalCondition).every(v => v !== null)
    if (!physicalComplete) return false
    if (!testResult.testingLevel) return false

    return true
  }

  const proceedToTesting = () => {
    if (!canProceedToTesting()) return
    setStep('testing')
  }

  const submitItem = async () => {
    setSaving(true)
    setError(null)

    try {
      // Format accessories
      const includedItems = includedItemsForSubmit(testResult.includedItems).filter(id => id !== 'other')
      const formattedAccessories = includedItems.map(id =>
        id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      )
      if (testResult.includedItems.includes('other') && testResult.otherAccessories?.trim()) {
        formattedAccessories.push(testResult.otherAccessories.trim())
      }

      const response = await api.post('/api/warehouse/manual-listing/queue-photography', {
        brand,
        model,
        productType,
        buyPrice: parseFloat(buyPrice) || 0,
        sellPrice: parseFloat(sellPrice) || 0,
        condition: testResult.overallCondition,
        physicalCondition: testResult.physicalCondition,
        testingLevel: testResult.testingLevel,
        checklistResults: testResult.checklistResults,
        accessories: formattedAccessories,
        notes: testResult.notes,
        needsRepair: testResult.needsRepair,
        repairIssues: testResult.repairIssues,
        ...linkRequestFields(tradeLink),
        quantity: parseInt(quantity, 10),
        reason: reason || undefined
      })

      setResult(response)
      setStep('success')
    } catch (err) {
      setError(err.message || 'Failed to queue for photography')
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setSearchQuery('')
    setSearchResults([])
    setHasSearched(false)
    setSelectedProduct(null)
    setBrand('')
    setModel('')
    setProductType('')
    setTestingBucket('')
    setBuyPrice('')
    setSellPrice('')
    setTradeLink(null)
    setQuantity('1')
    setReason('')
    setChecklist(null)
    setIncludedItemsOptions([])
    setTestResult({
      overallCondition: null,
      physicalCondition: {
        scratching: null,
        dings: null,
        colouration: null,
        cleanliness: null,
        integrity: null
      },
      testingLevel: null,
      checklistResults: {},
      includedItems: [],
      otherAccessories: '',
      notes: '',
      needsRepair: false,
      repairIssues: ''
    })
    setError(null)
    setResult(null)
    setStep('search')
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <Card style={{ padding: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Create Product</h2>
            <p style={{ color: theme.colors.grey500 }}>Search for an existing product or create a new one</p>
          </div>

          {/* Search Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Search Products
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  // Clear previous search results when user starts typing new query
                  if (hasSearched) {
                    setHasSearched(false)
                    setSearchResults([])
                  }
                }}
                onKeyDown={e => e.key === 'Enter' && searchProducts()}
                placeholder="e.g. Fender Stratocaster, Marshall JCM800..."
                style={{
                  flex: 1, padding: '14px 16px',
                  border: `1px solid ${theme.colors.grey300}`,
                  borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
                }}
              />
              <Button
                variant="primary"
                onClick={searchProducts}
                disabled={searchLoading || searchQuery.length < 2}
              >
                {searchLoading ? <Spinner size={16} /> : 'Search'}
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {hasSearched && (
            <div style={{ marginTop: '24px' }}>
              {searchResults.length > 0 ? (
                <>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: theme.colors.grey500 }}>
                    Found {searchResults.length} product{searchResults.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {searchResults.map(product => (
                      <button
                        key={product.id}
                        onClick={() => selectExistingProduct(product)}
                        style={{
                          width: '100%', padding: '14px 16px', marginBottom: '8px',
                          border: `1px solid ${theme.colors.grey200}`,
                          borderRadius: theme.radius.md, background: theme.colors.white,
                          cursor: 'pointer', textAlign: 'left',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{product.product_name}</div>
                          <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>{product.product_type}</div>
                        </div>
                        <span style={{ color: theme.colors.grey400 }}>›</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <Alert type="info">
                  No products found matching "{searchQuery}"
                </Alert>
              )}

              {/* Create New Product Option */}
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${theme.colors.grey200}` }}>
                <Button variant="secondary" fullWidth onClick={createNewProduct}>
                  + Create New Product: "{searchQuery}"
                </Button>
              </div>
            </div>
          )}

          {!hasSearched && (
            <div style={{ textAlign: 'center', padding: '24px', color: theme.colors.grey500 }}>
              Enter a product name to search
            </div>
          )}
        </Card>
      </div>
    )
  }

  // Entry Screen
  if (step === 'entry') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <Card style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
                {selectedProduct ? 'Add Existing Product' : 'Create New Product'}
              </h2>
              <p style={{ color: theme.colors.grey500 }}>
                {selectedProduct ? `Adding: ${selectedProduct.product_name}` : 'Enter product details'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setStep('search')}>← Back</Button>
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <TradeLinkPicker value={tradeLink} onChange={pickTrade} />

          {/* Brand */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Brand *
            </label>
            <input
              type="text"
              value={brand}
              onChange={e => setBrand(e.target.value)}
              placeholder="e.g. Fender, Marshall, Roland"
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
              }}
            />
          </div>

          {/* Model */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Model *
            </label>
            <input
              type="text"
              value={model}
              onChange={e => setModel(e.target.value)}
              placeholder="e.g. Stratocaster, JCM800, Juno-106"
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
              }}
            />
          </div>

          {/* Product Type */}
          {showProductTypeSelector ? (
            <ProductTypeSelector
              productTypes={productTypes}
              onSelect={handleProductTypeSelect}
              onCancel={() => setShowProductTypeSelector(false)}
              loading={productTypesLoading}
              accent={theme.colors.imBlue}
            />
          ) : (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                Product Type *
              </label>
              {productType ? (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 16px', background: theme.colors.grey100,
                  borderRadius: theme.radius.md
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{productType}</div>
                    {testingBucket && (
                      <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>
                        Bucket: {testingBucket}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={openProductTypeSelector}>Change</Button>
                </div>
              ) : (
                <Button variant="secondary" fullWidth onClick={openProductTypeSelector}>
                  Select Product Type
                </Button>
              )}
            </div>
          )}

          {/* Buy Price (Cost) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Cost / Buy Price *
            </label>
            <input
              type="number"
              value={buyPrice}
              onChange={e => setBuyPrice(e.target.value)}
              readOnly={isExtra || returnCostKnown}
              aria-label="Cost / Buy Price"
              placeholder="0.00"
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
              }}
            />
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="manual-listing-quantity" style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Quantity *
            </label>
            <input
              id="manual-listing-quantity"
              type="number"
              min="1"
              max={maxQuantity}
              step="1"
              value={quantity}
              readOnly={isReturn}
              onChange={e => setQuantity(e.target.value)}
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
              }}
            />
            {Number(quantity) > maxQuantity && (
              <div style={{ marginTop: '6px', fontSize: '13px', color: theme.colors.crimson }}>
                {tradeLink?.kind === 'line'
                  ? `This trade line has only ${maxQuantity} unit${maxQuantity === 1 ? '' : 's'}.`
                  : `A manual listing can hold at most ${maxQuantity} units.`}
              </div>
            )}
          </div>

          {/* Reason */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="manual-listing-reason" style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Why is this listed by hand? (optional)
            </label>
            <select
              id="manual-listing-reason"
              value={reason}
              onChange={e => pickReason(e.target.value)}
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px', outline: 'none', background: theme.colors.white
              }}
            >
              <option value="">Choose a reason</option>
              <option value="condition_split">Units on one trade line differ in condition</option>
              <option value="missed_in_flow">Missed in the normal flow</option>
              <option value="not_from_trade">Not from a trade</option>
              <option value="trade_extra" disabled={!isExtra}>Extra item from a trade</option>
              <option value="return_condition_change" disabled={!isReturn}>Return (change of condition needed)</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Preview */}
          {brand && model && (
            <Card style={{ marginBottom: '24px', background: theme.colors.grey100 }}>
              <div style={{ fontSize: '12px', color: theme.colors.grey500, marginBottom: '4px' }}>
                Product Name Preview
              </div>
              <div style={{ fontWeight: 600, fontSize: '16px' }}>{productName}</div>
            </Card>
          )}

          <Button
            variant="primary"
            fullWidth
            size="lg"
            disabled={!canProceedToTesting()}
            onClick={proceedToTesting}
          >
            Continue to Testing
          </Button>
        </Card>
      </div>
    )
  }

  // Testing Screen
  if (step === 'testing') {
    const isBroken = testResult.overallCondition === 'Broken'

    return (
      <div style={{ maxWidth: '700px', margin: '40px auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
              Create Product
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Testing: {productName}</h2>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setStep('entry')}>Back</Button>
        </div>

        {/* Item Info */}
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, fontSize: '18px', marginBottom: '8px' }}>{productName}</div>
          <div style={{ display: 'flex', gap: '16px', color: theme.colors.grey500, fontSize: '14px', flexWrap: 'wrap' }}>
            <span>Cost: {formatCurrency(parseFloat(buyPrice) || 0)}</span>
            {sellPrice && <span>Sell: {formatCurrency(parseFloat(sellPrice) || 0)}</span>}
            <Badge color="blue">{productType}</Badge>
          </div>
        </Card>

        {/* Overall Condition */}
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, marginBottom: '12px' }}>Overall Condition *</div>
          <ConditionPicker
            options={TESTING_CONDITION_OPTIONS}
            value={testResult.overallCondition}
            onChange={(val) => setTestResult(prev => ({ ...prev, overallCondition: val }))}
          />
        </Card>

        {/* Sell Price - Shows after condition selected */}
        {testResult.overallCondition && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px' }}>Sell Price</div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.grey500 }}>£</span>
              <input
                type="number"
                value={sellPrice}
                onChange={e => setSellPrice(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%', padding: '14px 16px 14px 28px',
                  border: `1px solid ${theme.colors.grey300}`,
                  borderRadius: theme.radius.md, fontSize: '15px', outline: 'none'
                }}
              />
            </div>
            <div style={{ fontSize: '12px', color: theme.colors.grey500, marginTop: '8px' }}>
              Based on condition: <strong>{testResult.overallCondition}</strong>
            </div>
          </Card>
        )}

        {/* Physical Condition - Only if not broken */}
        {testResult.overallCondition && !isBroken && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: 600, marginBottom: '16px' }}>Physical Condition Assessment *</div>
            {PHYSICAL_FIELDS.map(field => (
              <div key={field.key} style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>{field.label}</div>
                <OptionGroup
                  options={PHYSICAL_SCORE_OPTIONS}
                  value={testResult.physicalCondition[field.key]}
                  onChange={(val) => updatePhysicalCondition(field.key, val)}
                />
              </div>
            ))}
          </Card>
        )}

        {/* Testing Level - Only if not broken */}
        {testResult.overallCondition && !isBroken && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px' }}>
              Testing Level *
              <span style={{ fontWeight: 400, color: theme.colors.grey500, fontSize: '13px', marginLeft: '8px' }}>
                ({isElectrical ? 'Electrical' : 'Non-Electrical'} Item)
              </span>
            </div>
            <OptionGroup
              options={testingLevelOptions}
              value={testResult.testingLevel}
              onChange={(val) => setTestResult(prev => ({ ...prev, testingLevel: val }))}
              columns={testingLevelOptions.length}
            />
          </Card>
        )}

        {/* Checklist */}
        {testResult.testingLevel && testResult.testingLevel !== 'Untested' && checklist?.questions?.length > 0 && (
          <Card style={{ marginBottom: '20px' }} padding={false}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.colors.grey200}`, fontWeight: 600 }}>
              Test Checklist - {checklist.bucket}
            </div>
            <div style={{ padding: '12px' }}>
              {checklist.questions.map((q) => {
                const questionId = q.id || q.key
                const questionLabel = q.label || (typeof q === 'string' ? q : questionId)
                const result = testResult.checklistResults[questionId]
                return (
                  <div
                    key={questionId}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 14px', marginBottom: '8px',
                      background: result === 'OK' ? theme.colors.greenLight :
                        result === 'FAIL' ? theme.colors.redLight :
                        result === 'N/A' ? theme.colors.grey100 : theme.colors.white,
                      border: `1px solid ${theme.colors.grey200}`,
                      borderRadius: theme.radius.md
                    }}
                  >
                    <span style={{ fontWeight: 500, flex: 1 }}>{questionLabel}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {CHECKLIST_RESULTS.map(r => (
                        <button
                          key={r}
                          onClick={() => updateChecklistResult(questionId, r)}
                          style={{
                            padding: '6px 12px', borderRadius: theme.radius.sm,
                            background: result === r
                              ? (r === 'OK' ? theme.colors.greenOnions : r === 'FAIL' ? theme.colors.crimson : theme.colors.grey400)
                              : theme.colors.white,
                            color: result === r ? theme.colors.white : theme.colors.charcoal,
                            fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                            border: `1px solid ${theme.colors.grey200}`
                          }}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {/* Included items - required once a condition is chosen (W7), broken items included */}
        {includedItemsOptions.length > 0 && testResult.overallCondition && (
          <Card style={{ marginBottom: '20px' }}>
            <IncludedItemsPicker
              theme={theme}
              options={includedItemsOptions}
              selected={testResult.includedItems}
              otherText={testResult.otherAccessories}
              onToggle={toggleIncludedItem}
              onOtherChange={(text) => setTestResult(prev => ({ ...prev, otherAccessories: text }))}
            />
          </Card>
        )}

        {/* Needs Repair */}
        {testResult.overallCondition && !isBroken && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: testResult.needsRepair ? '16px' : 0 }}>
              <input
                type="checkbox"
                checked={testResult.needsRepair}
                disabled={isExtra || isReturn}
                onChange={(e) => setTestResult(prev => ({ ...prev, needsRepair: e.target.checked }))}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <label style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => { if (!isExtra && !isReturn) setTestResult(prev => ({ ...prev, needsRepair: !prev.needsRepair })) }}>
                Needs Repair{isExtra && ' (not for an extra item)'}{isReturn && ' (not for a returned item)'}
              </label>
            </div>
            {testResult.needsRepair && (
              <textarea
                value={testResult.repairIssues}
                onChange={(e) => setTestResult(prev => ({ ...prev, repairIssues: e.target.value }))}
                placeholder="Describe repair issues..."
                rows={3}
                style={{
                  width: '100%', padding: '12px',
                  border: `1px solid ${theme.colors.grey300}`,
                  borderRadius: theme.radius.md, fontSize: '14px',
                  resize: 'vertical', outline: 'none'
                }}
              />
            )}
          </Card>
        )}

        {/* Notes */}
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>Additional Notes</div>
          <textarea
            value={testResult.notes}
            onChange={e => setTestResult(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Enter any additional notes..."
            rows={3}
            style={{
              width: '100%', padding: '12px',
              border: `1px solid ${theme.colors.grey300}`,
              borderRadius: theme.radius.md, fontSize: '14px',
              resize: 'vertical', outline: 'none'
            }}
          />
        </Card>

        {error && <Alert type="error">{error}</Alert>}

        {/* Submit Button: without a trade, the first press asks staff to check */}
        <NoTradeSubmitGuard hasTrade={Boolean(tradeLink)} onSubmit={submitItem} onLinkTrade={() => setStep('entry')}>
          {press => (
            <Button
              variant={testResult.needsRepair ? 'warning' : 'success'}
              disabled={saving || !canSubmit()}
              onClick={press}
              fullWidth
              size="lg"
            >
              {saving ? <><Spinner size={18} light /> Saving...</> : (
                testResult.needsRepair ? 'Add to Repair List' :
                isBroken ? 'Create Listing (Broken)' :
                'Queue for Photography'
              )}
            </Button>
          )}
        </NoTradeSubmitGuard>

        {!canSubmit() && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: theme.colors.grey500, textAlign: 'center' }}>
            {!testResult.overallCondition && 'Select overall condition'}
            {testResult.overallCondition && !isBroken && !Object.values(testResult.physicalCondition).every(v => v) && ' • Complete physical assessment'}
            {testResult.overallCondition && !isBroken && !testResult.testingLevel && ' • Select testing level'}
          </div>
        )}
      </div>
    )
  }

  // Success Screen
  if (step === 'success') {
    return (
      <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>
            {result?.addedToRepairs ? '🔧' : '✅'}
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>
            {result?.addedToRepairs ? 'Added to Repairs' : 'Queued for Photography'}
          </h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '24px' }}>
            {result?.addedToRepairs
              ? `${productName} has been added to the repair list.`
              : `${productName} has been queued for photography.`
            }
          </p>

          <Card style={{ marginBottom: '24px', background: theme.colors.grey100 }}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>{productName}</div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Badge color="blue">{productType}</Badge>
              <Badge color={result?.addedToRepairs ? 'amber' : 'green'}>
                {result?.addedToRepairs ? 'Needs Repair' : testResult.overallCondition}
              </Badge>
            </div>
            {!result?.addedToRepairs && (
              <div style={{ marginTop: '12px', fontSize: '14px', color: theme.colors.grey500 }}>
                <div>Cost: {formatCurrency(parseFloat(buyPrice) || 0)}</div>
                {sellPrice && <div>Sell: {formatCurrency(parseFloat(sellPrice) || 0)}</div>}
              </div>
            )}
          </Card>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button variant="primary" fullWidth onClick={reset}>
              Add Another Item
            </Button>
            <Button variant="secondary" fullWidth onClick={() => window.location.href = result?.addedToRepairs ? '/warehouse/repairs' : '/warehouse'}>
              {result?.addedToRepairs ? 'View Repairs' : 'Done'}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return null
}

export default ManualListing
